import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, BookOpen, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const { results, topic, mode, weakAreas } = location.state || {};

  // Redirect if no results data
  useEffect(() => {
    if (!results) {
      navigate('/dashboard', { replace: true });
    }
  }, [results, navigate]);

  if (!results) {
    return null;
  }

  const correctCount = results.filter(r => r.is_correct).length;
  const totalCount = results.length;
  const percentage = Math.round((correctCount / totalCount) * 100);

  // Handler for going back to dashboard - uses replace to clear navigation state
  const handleBackToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-8 text-center">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
            percentage >= 80 ? 'bg-success/10' : percentage >= 60 ? 'bg-accent/10' : 'bg-error/10'
          }`}>
            {percentage >= 60 ? (
              <CheckCircle className={`w-10 h-10 ${
                percentage >= 80 ? 'text-success' : 'text-accent'
              }`} />
            ) : (
              <XCircle className="w-10 h-10 text-error" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2 font-sans">Quiz Complete!</h1>
          <p className="text-slate-600 mb-6">{topic}</p>
          <div className="text-5xl font-bold text-slate-900 mb-2">{percentage}%</div>
          <div className="text-slate-600">{correctCount} out of {totalCount} correct</div>
        </div>

        {mode === 'adaptive' && weakAreas && weakAreas.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-amber-900 mb-2 font-sans">Areas to Focus</h3>
            <p className="text-amber-700 text-sm mb-3">Based on your performance history, continue practicing these topics:</p>
            <div className="flex flex-wrap gap-2">
              {weakAreas.map((area, idx) => (
                <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-bold tracking-wide uppercase">
                  {area.subtopic}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {results.map((result, idx) => (
            <div
              key={idx}
              data-testid={`result-card-${idx}`}
              className="bg-white rounded-xl border border-slate-200 p-6"
            >
              <div className="flex items-start gap-3 mb-4">
                {result.is_correct ? (
                  <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                ) : (
                  <XCircle className="w-6 h-6 text-error flex-shrink-0 mt-1" />
                )}
                <div className="flex-1">
                  <div className={`text-sm font-semibold mb-1 ${
                    result.is_correct ? 'text-success' : 'text-error'
                  }`}>
                    {result.is_correct ? 'Correct' : 'Incorrect'}
                  </div>
                  <div className="text-sm text-slate-600 mb-3">
                    <strong>Correct Answer:</strong> {result.correct_answer}
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
                  <BookOpen className="w-3 h-3" />
                  Rationale
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{result.rationale}</p>
              </div>

              <div className="text-xs text-slate-500">
                <strong>Source:</strong> {result.source_reference}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-8">
          <Button
            data-testid="results-dashboard-button"
            onClick={handleBackToDashboard}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-semibold shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button
            data-testid="results-progress-button"
            onClick={() => navigate('/progress')}
            variant="outline"
            className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 h-12 px-8 rounded-full font-semibold"
          >
            View Progress
          </Button>
        </div>
      </div>
    </div>
  );
}